import { useEffect } from 'react'
import { contact, identity, portrait } from '../data/portfolio'

const TITLE = 'Nigel Grierson | Executive & Board Leadership'
const DESCRIPTION =
  'Nigel Grierson — executive and board leader with extensive leadership experience, board experience, and a distinguished professional career.'
const SITE_NAME = 'Nigel Grierson'
const CANONICAL = contact.websiteUrl
const STRUCTURED_DATA_ID = 'nigel-grierson-structured-data'

function setMetaByName(name: string, content: string): void {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute('name', name)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

function setMetaByProperty(property: string, content: string): void {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute('property', property)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

function setLink(rel: string, href: string, type?: string): void {
  const selector = type ? `link[rel="${rel}"][type="${type}"]` : `link[rel="${rel}"]`
  let tag = document.head.querySelector<HTMLLinkElement>(selector)
  if (!tag) {
    tag = document.createElement('link')
    tag.setAttribute('rel', rel)
    if (type) tag.setAttribute('type', type)
    document.head.appendChild(tag)
  }
  tag.setAttribute('href', href)
}

/**
 * Applies the site identity, SEO, social and structured-data metadata for
 * nigelgrierson1.com. Purely head-level: it does not affect rendered UI.
 */
export function useSiteMeta(): void {
  useEffect(() => {
    // 1 & 2 — Browser tab and search result title
    document.title = TITLE

    // 3 — Meta description and core identity
    setMetaByName('description', DESCRIPTION)
    setMetaByName('author', SITE_NAME)
    setMetaByName('robots', 'index, follow, max-image-preview:large')
    setMetaByName('theme-color', '#0b0118')
    setMetaByName('application-name', SITE_NAME)
    setMetaByName('apple-mobile-web-app-title', SITE_NAME)

    // 4 — Open Graph
    setMetaByProperty('og:type', 'profile')
    setMetaByProperty('og:title', TITLE)
    setMetaByProperty('og:description', DESCRIPTION)
    setMetaByProperty('og:site_name', SITE_NAME)
    setMetaByProperty('og:url', CANONICAL)
    setMetaByProperty('og:locale', 'en_GB')
    setMetaByProperty('og:image', portrait)
    setMetaByProperty('og:image:alt', `Portrait of ${SITE_NAME}`)
    setMetaByProperty('profile:first_name', 'Nigel')
    setMetaByProperty('profile:last_name', 'Grierson')

    // 5 — Twitter / X card
    setMetaByName('twitter:card', 'summary_large_image')
    setMetaByName('twitter:title', TITLE)
    setMetaByName('twitter:description', DESCRIPTION)
    setMetaByName('twitter:image', portrait)
    setMetaByName('twitter:image:alt', `Portrait of ${SITE_NAME}`)

    // 6 — Canonical URL (production domain)
    setLink('canonical', CANONICAL)

    // 8 — Favicon / browser metadata
    setLink('icon', portrait)
    setLink('apple-touch-icon', portrait)

    // 7 — Schema.org structured data, using only facts already on the site
    const structuredData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Person',
          '@id': `${CANONICAL}/#person`,
          name: SITE_NAME,
          givenName: 'Nigel',
          familyName: 'Grierson',
          url: CANONICAL,
          image: portrait,
          jobTitle: identity.role,
          description: DESCRIPTION,
          email: `mailto:${contact.email}`,
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'London',
            addressCountry: 'GB',
          },
          sameAs: [contact.linkedinUrl],
          affiliation: {
            '@type': 'Organization',
            name: 'Frontier IP Group plc',
          },
          alumniOf: [
            { '@type': 'EducationalOrganization', name: 'INSEAD' },
            { '@type': 'EducationalOrganization', name: 'University of Bristol' },
          ],
          knowsAbout: [
            'Board governance',
            'Venture capital',
            'Technology investment',
            'Capital allocation',
            'Corporate finance',
            'Mergers and acquisitions',
          ],
        },
        {
          '@type': 'WebSite',
          '@id': `${CANONICAL}/#website`,
          url: CANONICAL,
          name: SITE_NAME,
          description: DESCRIPTION,
          inLanguage: 'en-GB',
          publisher: { '@id': `${CANONICAL}/#person` },
          about: { '@id': `${CANONICAL}/#person` },
        },
        {
          '@type': 'ProfilePage',
          '@id': `${CANONICAL}/#profilepage`,
          url: CANONICAL,
          name: TITLE,
          description: DESCRIPTION,
          isPartOf: { '@id': `${CANONICAL}/#website` },
          mainEntity: { '@id': `${CANONICAL}/#person` },
        },
      ],
    }

    let script = document.getElementById(STRUCTURED_DATA_ID) as HTMLScriptElement | null
    if (!script) {
      script = document.createElement('script')
      script.id = STRUCTURED_DATA_ID
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(structuredData)

    // 9 — Remove any stale generated metadata left by the project scaffold
    const staleTitlePattern = /magic\s*patterns|don't change color|dont change color|untitled/i
    document.head
      .querySelectorAll<HTMLMetaElement>('meta[name], meta[property]')
      .forEach((tag) => {
        const content = tag.getAttribute('content') ?? ''
        if (staleTitlePattern.test(content)) {
          tag.remove()
        }
      })

    document.documentElement.setAttribute('lang', 'en-GB')
  }, [])
}
